import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getSkillDetails } from '../data/skillDetailedData';
import { 
  FaArrowLeft, 
  FaBook, 
  FaRoute, 
  FaTools, 
  FaExternalLinkAlt, 
  FaBriefcase, 
  FaMoneyBillWave,
  FaLightbulb,
  FaGraduationCap
} from 'react-icons/fa';

const SkillDetails = () => {
  const { domain, skill } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (domain && skill) {
      // Decode URI components in case they have encoded characters
      const decodedDomain = decodeURIComponent(domain);
      const decodedSkill = decodeURIComponent(skill);
      const details = getSkillDetails(decodedDomain, decodedSkill);
      setData(details);
    }
  }, [domain, skill]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white pt-24 pb-12 rounded-b-[3rem] shadow-lg mb-8">
        <div className="container-responsive">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <FaArrowLeft className="mr-2" /> Back to Trending
          </button>
          
          <div className="bg-white/10 backdrop-blur-md inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4 border border-white/20">
            {decodeURIComponent(domain)}
          </div>
          
          <h1 className="heading-primary mb-4">{decodeURIComponent(skill)}</h1>
          
          <div className="flex flex-wrap gap-6 text-white/90">
             {data.salaryRange && (
               <div className="flex items-center">
                 <FaMoneyBillWave className="mr-2 opacity-80" />
                 <span>Avg. Salary: {data.salaryRange}</span>
               </div>
             )}
             <div className="flex items-center">
               <FaBriefcase className="mr-2 opacity-80" />
               <span>High Demand</span>
             </div>
          </div>
        </div>
      </div>

      <div className="container-responsive">
        {/* Tabs */}
        <div className="flex overflow-x-auto space-x-2 mb-8 pb-2">
          {['overview', 'roadmap', 'resources', 'related'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === tab 
                  ? 'bg-primary-600 text-white shadow-md' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-8">
            
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="card p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <h2 className="heading-tertiary text-gray-900 mb-4 flex items-center">
                    <FaLightbulb className="mr-3 text-yellow-500" />
                    About this Skill
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {data.description}
                  </p>
                </div>

                <div className="card p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <h2 className="heading-tertiary text-gray-900 mb-4 flex items-center">
                    <FaBriefcase className="mr-3 text-blue-500" />
                    Career Outlook
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    {data.careerOutlook}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'roadmap' && (
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                  <FaRoute className="mr-3 text-green-500" />
                  Learning Path
                </h2>
                <div className="relative border-l-2 border-primary-200 ml-3 space-y-12">
                  {data.roadmap?.map((step, idx) => (
                    <div key={idx} className="relative pl-8">
                      <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-primary-500 border-4 border-white shadow-sm"></div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{step.phase}</h3>
                      <ul className="space-y-2">
                        {step.items?.map((item, i) => (
                          <li key={i} className="flex items-center text-gray-700 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                            <div className="w-1.5 h-1.5 rounded-full bg-secondary-400 mr-2"></div>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'resources' && (
              <div className="space-y-6">
                 <h2 className="text-2xl font-bold text-gray-900 flex items-center mb-4">
                  <FaBook className="mr-3 text-purple-500" />
                  Curated Resources
                </h2>
                {data.resources?.length > 0 ? (
                  data.resources.map((res, idx) => (
                    <div key={idx} className="flex flex-col md:flex-row bg-white p-4 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                      {res.image && (
                         <img src={res.image} alt={res.title} className="w-full md:w-32 h-32 object-cover rounded-lg mb-4 md:mb-0 md:mr-6" />
                      )}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                           <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">{res.title}</h3>
                           <span className="text-xs font-semibold px-2 py-1 bg-blue-50 text-blue-600 rounded-full">{res.category || 'Resource'}</span>
                        </div>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{res.description}</p>
                        <a href={res.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary-600 font-medium hover:underline text-sm">
                          View Resource <FaExternalLinkAlt className="ml-1 w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic">No specific resources listed yet. Check the Documentation section.</p>
                )}
                
                <div className="mt-8 text-center border-t pt-6 border-gray-100">
                  <Link 
                    to="/resource-hub" 
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 transition-colors duration-200"
                  >
                    <FaBook className="mr-2" />
                    Explore All Resources in Hub
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'related' && (
               <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                   <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <FaTools className="mr-3 text-orange-500" />
                    Related Skills & Tools
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {data.relatedSkills?.map((skill, idx) => (
                      <span key={idx} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-default border border-gray-200">
                        {skill}
                      </span>
                    ))}
                  </div>
               </div>
            )}

          </div>

          {/* Sidebar / External Links */}
          <div className="space-y-6">
             <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <FaGraduationCap className="mr-2 text-indigo-500" />
                  Official Documentation
                </h3>
                <div className="space-y-3">
                  {data.docs?.map((doc, idx) => (
                    <a 
                      key={idx}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-indigo-50 group transition-colors border border-transparent hover:border-indigo-100"
                    >
                      <span className="text-gray-700 group-hover:text-indigo-700 truncate mr-2">{doc.title}</span>
                      <FaExternalLinkAlt className="w-3 h-3 text-gray-400 group-hover:text-indigo-500 flex-shrink-0" />
                    </a>
                  ))}
                  {(!data.docs || data.docs.length === 0) && (
                    <p className="text-sm text-gray-500">No documentation links available.</p>
                  )}
                </div>
                
                <hr className="my-6 border-gray-100" />
                
                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
                <Link to="/mentors" className="block w-full text-center py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 font-medium mb-3">
                  Find a Mentor
                </Link>
                <div className="text-center">
                     <Link to="/skills-analysis" className="text-sm text-primary-600 hover:underline">
                        Check your Skill Gap
                     </Link>
                </div>

             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SkillDetails;
